export const questionsData = {
  print: [
    {
      id: 'P1',
      title: 'Fleet Visibility & Control',
      question: 'Do you have full visibility and control over your print environment, including costs, usage, and device performance?',
      options: [
        { value: 1, label: 'No visibility', desc: 'We don\'t track devices or costs' },
        { value: 2, label: 'Limited', desc: 'Some awareness but no central tracking' },
        { value: 3, label: 'Moderate', desc: 'We track some metrics manually' },
        { value: 4, label: 'Good', desc: 'Centralized tracking with regular reports' },
        { value: 5, label: 'Excellent', desc: 'Full visibility with automated monitoring' }
      ],
      impact: ['Cost leakage from untracked devices', 'Overspending on supplies', 'Shadow IT risk'],
      followup: 'If a printer goes down today, what happens operationally? Who handles it?',
      insight: 'Organizations with full fleet visibility reduce print costs by 20-30%'
    },
    {
      id: 'P2',
      title: 'Print Management & Support',
      question: 'Is your print environment centrally managed with proactive monitoring and support?',
      options: [
        { value: 1, label: 'Break-fix', desc: 'We call someone when it breaks' },
        { value: 2, label: 'Reactive', desc: 'IT handles issues as they arise' },
        { value: 3, label: 'Managed', desc: 'Basic monitoring in place' },
        { value: 4, label: 'Proactive', desc: 'Automated alerts and scheduled maintenance' },
        { value: 5, label: 'Optimized', desc: 'Predictive maintenance and optimization' }
      ],
      impact: ['Emergency service calls cost 3x more', 'Productivity loss during downtime', 'User frustration'],
      followup: 'How long does it typically take to resolve a printer issue? Hours? Days?',
      insight: 'Proactive print management reduces downtime by 40%'
    },
    {
      id: 'P3',
      title: 'Document Workflows',
      question: 'Are your document workflows digitized and automated (scan-to-cloud, digital forms, automated routing)?',
      options: [
        { value: 1, label: 'Paper-based', desc: 'Most processes still use paper' },
        { value: 2, label: 'Minimal', desc: 'Some basic scanning' },
        { value: 3, label: 'Partial', desc: 'Key workflows digitized' },
        { value: 4, label: 'Mostly digital', desc: 'Most workflows automated' },
        { value: 5, label: 'Fully digital', desc: 'End-to-end automation' }
      ],
      impact: ['$20+ cost per paper document handled', 'Process delays', 'Compliance risk'],
      followup: 'Walk me through what happens when a document needs multiple approvals.',
      insight: 'Digital workflows reduce document processing time by 80%'
    },
    {
      id: 'P4',
      title: 'Print Security',
      question: 'Do you have secure print controls (badge/PIN release, audit trails, data encryption)?',
      options: [
        { value: 1, label: 'None', desc: 'Anyone can pick up any print job' },
        { value: 2, label: 'Basic', desc: 'Some awareness but no controls' },
        { value: 3, label: 'Partial', desc: 'Secure print on some devices' },
        { value: 4, label: 'Good', desc: 'Secure print deployed widely' },
        { value: 5, label: 'Complete', desc: 'Full security with audit trails' }
      ],
      impact: ['60% of data breaches involve printed documents', 'Compliance violations', 'Sensitive data exposure'],
      followup: 'Have you ever had sensitive documents left unclaimed on a printer?',
      insight: 'Secure print prevents sensitive documents from being left unclaimed'
    },
    {
      id: 'P5',
      title: 'Cost Allocation & Accountability',
      question: 'Can you track and allocate print costs by department, user, or project?',
      options: [
        { value: 1, label: 'No tracking', desc: 'Print costs are just overhead' },
        { value: 2, label: 'Minimal', desc: 'We know total spend only' },
        { value: 3, label: 'Basic', desc: 'Some department-level tracking' },
        { value: 4, label: 'Detailed', desc: 'User and department tracking' },
        { value: 5, label: 'Complete', desc: 'Full allocation with reporting' }
      ],
      impact: ['No accountability = no behavior change', 'Hidden costs', 'Budget overruns'],
      followup: 'Do department heads know how much their teams spend on printing?',
      insight: 'Organizations reduce print volumes 15-25% when users see their usage'
    }
  ],
  it: [
    {
      id: 'I1',
      title: 'Endpoint Management',
      question: 'Are all devices (PCs, laptops, mobile) centrally managed with remote capabilities?',
      options: [
        { value: 1, label: 'No management', desc: 'Devices are not tracked or managed' },
        { value: 2, label: 'Limited', desc: 'Some devices managed manually' },
        { value: 3, label: 'Partial', desc: 'MDM for some device types' },
        { value: 4, label: 'Good', desc: 'Most devices under central management' },
        { value: 5, label: 'Complete', desc: 'All devices managed with full visibility' }
      ],
      impact: ['Unmanaged endpoints 3x more likely to be compromised', 'Slow support response', 'Security blind spots'],
      followup: 'If an employee loses their laptop today, how quickly can you secure it?',
      insight: 'Remote management enables 50% faster issue resolution'
    },
    {
      id: 'I2',
      title: 'Cloud Adoption',
      question: 'Have you migrated critical workloads to cloud platforms (M365, Azure, AWS)?',
      options: [
        { value: 1, label: 'On-premise', desc: 'Everything runs locally' },
        { value: 2, label: 'Minimal', desc: 'Email only in cloud' },
        { value: 3, label: 'Partial', desc: 'Some apps in cloud' },
        { value: 4, label: 'Mostly cloud', desc: 'Core systems migrated' },
        { value: 5, label: 'Cloud-first', desc: 'Strategic cloud architecture' }
      ],
      impact: ['On-premise = higher maintenance costs', 'Limited scalability', '95% vs 99.9% uptime'],
      followup: 'What happens to your business if your server room has an issue?',
      insight: 'Cloud-first organizations experience 20% lower IT costs'
    },
    {
      id: 'I3',
      title: 'Backup & Disaster Recovery',
      question: 'Do you have tested, automated backups with defined recovery time objectives?',
      options: [
        { value: 1, label: 'No backups', desc: 'We hope nothing happens' },
        { value: 2, label: 'Manual', desc: 'Occasional manual backups' },
        { value: 3, label: 'Automated', desc: 'Backups run but untested' },
        { value: 4, label: 'Tested', desc: 'Regular backups with periodic tests' },
        { value: 5, label: 'Complete', desc: 'Tested DR plan with defined RTO/RPO' }
      ],
      impact: ['67% of businesses losing data 10+ days file bankruptcy', 'Compliance failures', 'Reputation damage'],
      followup: 'When was the last time you actually tested restoring from backup?',
      insight: 'Tested backups ensure business continuity'
    },
    {
      id: 'I4',
      title: 'Network Infrastructure',
      question: 'Is your network infrastructure modern, monitored, and scalable?',
      options: [
        { value: 1, label: 'Aging', desc: 'Old equipment, no monitoring' },
        { value: 2, label: 'Basic', desc: 'Functional but aging' },
        { value: 3, label: 'Adequate', desc: 'Meets current needs' },
        { value: 4, label: 'Modern', desc: 'Updated with monitoring' },
        { value: 5, label: 'Advanced', desc: 'SD-WAN, full visibility, scalable' }
      ],
      impact: ['Legacy networks cause 40% of outages', 'Performance bottlenecks', 'Security vulnerabilities'],
      followup: 'How old is your network equipment? Do you know when it was last updated?',
      insight: 'Modern SD-WAN reduces network costs 30% while improving performance'
    },
    {
      id: 'I5',
      title: 'IT Support Model',
      question: 'Do you have proactive IT support with SLAs and regular strategic reviews?',
      options: [
        { value: 1, label: 'Break-fix', desc: 'We call someone when it breaks' },
        { value: 2, label: 'Reactive', desc: 'Internal IT handles issues' },
        { value: 3, label: 'Managed', desc: 'Outsourced with basic SLAs' },
        { value: 4, label: 'Proactive', desc: 'Strategic partnership with reviews' },
        { value: 5, label: 'vCIO', desc: 'Full strategic IT alignment' }
      ],
      impact: ['Reactive IT costs 3-5x more', 'No strategic alignment', 'Constant firefighting'],
      followup: 'Who is responsible for making sure your technology supports your business goals?',
      insight: 'Strategic vCIO guidance aligns IT with business goals'
    }
  ],
  cyber: [
    {
      id: 'C1',
      title: 'Endpoint Protection',
      question: 'Do you have advanced endpoint protection (EDR/XDR) on all devices?',
      options: [
        { value: 1, label: 'Basic AV', desc: 'Just antivirus software' },
        { value: 2, label: 'Limited', desc: 'AV with some management' },
        { value: 3, label: 'Managed AV', desc: 'Centrally managed antivirus' },
        { value: 4, label: 'EDR', desc: 'Endpoint detection & response' },
        { value: 5, label: 'XDR', desc: 'Extended detection with 24/7 SOC' }
      ],
      impact: ['Traditional AV catches only 40% of threats', 'No visibility into attacks', 'Slow incident response'],
      followup: 'If malware got onto a computer today, how would you know? How fast could you respond?',
      insight: 'EDR/XDR detects and responds to sophisticated attacks in real-time'
    },
    {
      id: 'C2',
      title: 'Security Awareness',
      question: 'Do all employees receive regular security awareness training and phishing simulations?',
      options: [
        { value: 1, label: 'None', desc: 'No formal training' },
        { value: 2, label: 'Annual', desc: 'Once a year maybe' },
        { value: 3, label: 'Quarterly', desc: 'Regular training sessions' },
        { value: 4, label: 'Ongoing', desc: 'Continuous training with testing' },
        { value: 5, label: 'Culture', desc: 'Security-first culture with metrics' }
      ],
      impact: ['91% of cyberattacks start with phishing', 'Human error is #1 cause of breaches', 'Compliance requirements'],
      followup: 'If I sent a phishing email to your team right now, how many would click?',
      insight: 'Trained employees reduce successful phishing attacks by 70%'
    },
    {
      id: 'C3',
      title: 'Access Management',
      question: 'Do you use multi-factor authentication (MFA) and least-privilege access controls?',
      options: [
        { value: 1, label: 'Passwords only', desc: 'No MFA anywhere' },
        { value: 2, label: 'Limited MFA', desc: 'MFA for some systems' },
        { value: 3, label: 'Partial', desc: 'MFA for critical systems' },
        { value: 4, label: 'Widespread', desc: 'MFA everywhere, some access controls' },
        { value: 5, label: 'Zero Trust', desc: 'Full MFA + least privilege + monitoring' }
      ],
      impact: ['MFA blocks 99.9% of account compromise', 'Single most effective security control', 'Insurance requirements'],
      followup: 'If an employee\'s password was stolen today, what could an attacker access?',
      insight: 'MFA is the single most effective security control available'
    },
    {
      id: 'C4',
      title: 'Vulnerability Management',
      question: 'Are systems regularly patched and scanned for vulnerabilities?',
      options: [
        { value: 1, label: 'Manual/ad-hoc', desc: 'Updates when we remember' },
        { value: 2, label: 'Monthly', desc: 'Occasional patching efforts' },
        { value: 3, label: 'Regular', desc: 'Scheduled patching process' },
        { value: 4, label: 'Automated', desc: 'Automated patching with scanning' },
        { value: 5, label: 'Continuous', desc: 'Continuous scanning and remediation' }
      ],
      impact: ['60% of breaches exploit known vulnerabilities', 'Attackers scan for unpatched systems', 'Compliance failures'],
      followup: 'How long does it typically take to apply a critical security patch?',
      insight: 'Automated patching closes the window attackers exploit'
    },
    {
      id: 'C5',
      title: 'Incident Response',
      question: 'Do you have a documented incident response plan that has been tested?',
      options: [
        { value: 1, label: 'None', desc: 'No plan exists' },
        { value: 2, label: 'Informal', desc: 'We\'d figure it out' },
        { value: 3, label: 'Documented', desc: 'Plan exists but untested' },
        { value: 4, label: 'Tested', desc: 'Documented and tested annually' },
        { value: 5, label: 'Mature', desc: 'Regular drills with continuous improvement' }
      ],
      impact: ['Untested plans fail 60% of the time', 'Average breach cost $4.45M', 'Faster containment = less damage'],
      followup: 'If you got a ransomware attack at 2am, who gets called? What happens next?',
      insight: 'Tested IR plans contain breaches 54% faster, saving $1.2M on average'
    }
  ]
};